import { useState } from "react";
import { useLang } from "../lib/i18n";
import { PageHeader, Section, Field } from "../components/common";

const TYPES = [
  ["stucco_3coat", "Stucco – new 3-coat system", "Estuco – sistema nuevo de 3 capas"],
  ["stucco_repair", "Stucco – repair / patch", "Estuco – reparación / parche"],
  ["paint_ext", "Painting – exterior (stucco)", "Pintura – exterior (estuco)"],
  ["paint_int", "Painting – interior", "Pintura – interior"],
  ["waterproof", "Waterproofing (elastomeric)", "Impermeabilización (elastomérico)"],
];

function calc(type, sqft, coats, waste, lf) {
  const w = 1 + waste / 100;
  const c = (n) => Math.ceil(n * w);
  const caulkLf = lf || sqft / 10;
  const r = [];
  if (type === "stucco_3coat") {
    r.push(["Lath (27\"x97\" sheets, ~16 sq ft net)", "Malla metálica (hojas 27\"x97\", ~16 pie² netos)", c(sqft / 16), "sheets / hojas"]);
    r.push(["Weather barrier paper (2 layers, ~324 sq ft/roll)", "Papel barrera (2 capas, ~324 pie²/rollo)", c((sqft * 2) / 324), "rolls / rollos"]);
    r.push(["Base coat stucco 80 lb (scratch + brown, ~12.5 sq ft/bag)", "Estuco base 80 lb (capa base + media, ~12.5 pie²/saco)", c(sqft / 12.5), "bags / sacos"]);
    r.push(["Finish coat 90 lb (~80 sq ft/bag)", "Acabado 90 lb (~80 pie²/saco)", c(sqft / 80), "bags / sacos"]);
    r.push(["Mesh for corners/openings (~475 sq ft/roll)", "Malla para esquinas/aberturas (~475 pie²/rollo)", c(sqft / 475 + 0.5), "rolls / rollos"]);
  }
  if (type === "stucco_repair") {
    r.push(["Lath (sheets, ~16 sq ft net)", "Malla metálica (hojas, ~16 pie² netos)", c(sqft / 16), "sheets / hojas"]);
    r.push(["Patch / base stucco 80 lb (~12.5 sq ft/bag)", "Estuco base/parche 80 lb (~12.5 pie²/saco)", c(sqft / 12.5), "bags / sacos"]);
    r.push(["Finish coat 90 lb (~80 sq ft/bag)", "Acabado 90 lb (~80 pie²/saco)", c(sqft / 80), "bags / sacos"]);
    r.push(["Fiberglass mesh (~475 sq ft/roll)", "Malla de fibra de vidrio (~475 pie²/rollo)", c(sqft / 475 + 0.5), "rolls / rollos"]);
    r.push(["Primer (~250 sq ft/gal)", "Primer (~250 pie²/gal)", c(sqft / 250), "gal"]);
    r.push(["Paint (~250 sq ft/gal/coat)", "Pintura (~250 pie²/gal/capa)", c((sqft * coats) / 250), "gal"]);
  }
  if (type === "paint_ext" || type === "paint_int") {
    const cov = type === "paint_ext" ? 250 : 350;
    r.push([`Primer (~${cov} sq ft/gal, 1 coat)`, `Primer (~${cov} pie²/gal, 1 capa)`, c(sqft / cov), "gal"]);
    r.push([`Paint (~${cov} sq ft/gal × ${coats} coats)`, `Pintura (~${cov} pie²/gal × ${coats} capas)`, c((sqft * coats) / cov), "gal"]);
  }
  if (type === "waterproof") {
    r.push(["Elastomeric coating (~100 sq ft/gal/coat)", "Recubrimiento elastomérico (~100 pie²/gal/capa)", c((sqft * coats) / 100), "gal"]);
    r.push(["Primer / sealer (~250 sq ft/gal)", "Primer / sellador (~250 pie²/gal)", c(sqft / 250), "gal"]);
    r.push(["Mesh for cracks (~475 sq ft/roll)", "Malla para grietas (~475 pie²/rollo)", c(sqft / 1000 + 0.5), "rolls / rollos"]);
  }
  r.push(["Caulk tubes (10 oz, ~50 linear ft/tube)", "Tubos de calafateo (10 oz, ~50 pies lineales/tubo)", c(caulkLf / 50), "tubes / tubos"]);
  return r;
}

export default function MaterialCalculator() {
  const { t, lang } = useLang();
  const [type, setType] = useState("stucco_repair");
  const [sqft, setSqft] = useState(200);
  const [coats, setCoats] = useState(2);
  const [waste, setWaste] = useState(10);
  const [lf, setLf] = useState("");
  const rows = sqft > 0 ? calc(type, Number(sqft), Number(coats), Number(waste), Number(lf) || 0) : [];
  return (
    <>
      <PageHeader title={t("Material Calculator", "Calculadora de materiales")} subtitle={t("Internal estimating help only. Always verify with manufacturer coverage.", "Solo ayuda interna para estimar. Verifique siempre con el rendimiento del fabricante.")} />
      <div className="grid lg:grid-cols-[360px_1fr] gap-4 items-start">
        <Section title={t("Job", "Trabajo")} testid="calc-form">
          <div className="grid gap-3 p-4">
            <Field label={t("Project type", "Tipo de proyecto")}>
              <select className="inp" value={type} onChange={(e) => setType(e.target.value)} data-testid="calc-type-select">
                {TYPES.map(([k, en, es]) => <option key={k} value={k}>{lang === "es" ? es : en}</option>)}
              </select>
            </Field>
            <Field label={t("Square footage", "Pies cuadrados")}><input className="inp mono text-xl" type="number" inputMode="decimal" value={sqft} onChange={(e) => setSqft(e.target.value)} data-testid="calc-sqft-input" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("Coats", "Capas")}><input className="inp" type="number" value={coats} onChange={(e) => setCoats(e.target.value)} data-testid="calc-coats-input" /></Field>
              <Field label={t("Waste %", "Desperdicio %")}><input className="inp" type="number" value={waste} onChange={(e) => setWaste(e.target.value)} data-testid="calc-waste-input" /></Field>
            </div>
            <Field label={t("Caulk linear feet (optional)", "Pies lineales de calafateo (opcional)")}><input className="inp" type="number" value={lf} onChange={(e) => setLf(e.target.value)} data-testid="calc-lf-input" /></Field>
          </div>
        </Section>
        <Section title={t("Estimated quantities", "Cantidades estimadas")} testid="calc-results">
          {rows.map(([en, es, q, unit], i) => (
            <div key={i} className="row-link" data-testid={`calc-result-${i}`}>
              <span className="flex-1 text-sm">{lang === "es" ? es : en}</span>
              <span className="mono text-2xl font-semibold text-[#D9531E]">{q}</span>
              <span className="text-xs text-slate-500 w-24">{unit}</span>
            </div>
          ))}
        </Section>
      </div>
    </>
  );
}
